/**
 * Service-worker update pipeline.
 *
 * LESSON PINNED AT THE SCENE (Journal post-mortem §7, §8):
 *  - Never apply an update mid-interaction. A stylist halfway through cataloguing an
 *    item does not want the page to reload under them; the update waits for a safe
 *    moment (next cold boot, or an explicit tap).
 *  - Exactly one reload. `controllerchange` fires more than once in some browsers,
 *    and an unguarded `location.reload()` there is an infinite loop. The guard lives
 *    in sessionStorage so it survives the very reload it triggers.
 */
import { registerSW } from 'virtual:pwa-register';
import { ledger, reloadGuard } from '../platform/browser.js';
import { markUpdatePending } from './pending.js';

export interface UpdateHandle {
  /** Called when a new version is waiting. Show a non-blocking prompt. */
  onWaiting: (apply: () => void) => void;
}

/**
 * Once this session's single reload has been spent, asking again is noise.
 *
 * FOUND IN PRODUCTION 26 Jul 2026: `onNeedRefresh` fires on repeated service-worker
 * events, so every one of them re-prompted, the guard refused, and the refusal was
 * logged as an error -- six times in seventeen seconds, until it was the most frequent
 * entry in the ledger. The guard was right every time. The reporting was wrong every
 * time, and a ledger full of correct behaviour hides the incorrect kind.
 */
function alreadySpentThisSession(): boolean {
  return reloadGuard.hasReloaded('sw-update');
}

export function setupUpdates(handle: UpdateHandle): void {
  if (!('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      /**
       * TIMING INSTRUMENTATION (maintainer, 19 Aug — "the reload button feels slower than the old
       * native popup did"). The S2-c change only swapped `window.confirm` for the redesign Confirm;
       * the apply (`updateSW(true)` → skip-waiting → the one guarded reload) is byte-for-byte the
       * same, and the confirm sheet has no entrance animation. So the reload STEP cannot have got
       * slower — but rather than argue that, this measures it in the field: `detected→tap` is the
       * person's own decision time; `tap→swap` is the app's real apply latency (skip-waiting to
       * `controllerchange`, right before the reload). A note, not an error — an outcome to read.
       */
      const detectedAt = performance.now();
      if (alreadySpentThisSession()) {
        // Do not prompt again. Surface it as state the UI can render, and record it
        // once per session as a NOTE -- an outcome, not a failure.
        markUpdatePending();
        if (reloadGuard.markAndAllow('sw-update-noted')) {
          ledger.note('UPDATE_PENDING_MANUAL_RELOAD', 'sw-update', {
            userMessage: 'A new version is ready. It will apply next time you open the app.',
            notified: true,
          });
        }
        return;
      }

      handle.onWaiting(() => {
        const tappedAt = performance.now();
        if (!reloadGuard.markAndAllow('sw-update')) {
          // Refusing is right: a second reload here is a loop, and a loop is worse
          // than a stale tab. Recorded as a note, not an error -- see above.
          markUpdatePending();
          ledger.note('UPDATE_RELOAD_REFUSED', 'sw-update', {
            userMessage: 'A new version is ready. Close and reopen the app to apply it.',
            notified: true,
          });
          return;
        }
        // Stamp the swap the instant the new worker takes control — this fires just before the
        // plugin's own `controllerchange` listener reloads, so the note persists first (a
        // localStorage write; the reload is a scheduled navigation that lets this task finish).
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.addEventListener(
            'controllerchange',
            () => {
              const swappedAt = performance.now();
              ledger.note('UPDATE_TIMING', 'sw-update', {
                userMessage: 'Update applied.',
                notified: false,
                // ⚠️ context, NOT detail: `toSupportText` renders `context` for every entry but
                // only renders `detail` for UNCLASSIFIED errors — a note's detail would be dropped
                // from the copyable Diagnostics payload, which is the one place these numbers are read.
                context: {
                  detectedToTapMs: Math.round(tappedAt - detectedAt),
                  tapToSwapMs: Math.round(swappedAt - tappedAt),
                },
              });
            },
            { once: true },
          );
        }
        void updateSW(true);
      });
    },
    onRegisterError(error) {
      // A failed SW registration used to be invisible; now it is a ledger entry.
      ledger.record(error, 'sw-register', { notified: false });
    },
  });

  /**
   * ASK TO BE CLAIMED IF NOBODY DID (0.1.192).
   *
   * ⚠️ THE BUG THIS FIXES WAS DIAGNOSED FROM CI, not reasoned out — see `sw.ts`'s CLAIM
   * handler for the state dump that named it. `clientsClaim()` runs once, at activate; a
   * document whose navigation began before there was an active worker but committed after
   * activate had already run is claimed by nobody, and stays uncontrolled for its whole life.
   *
   * An uncontrolled page has no offline shell and no read cache while looking perfectly
   * healthy — a silent loss of the entire offline tier (G10). Four releases were spent
   * reasoning about it as a timing flake in a test; it is a real defect in the field.
   *
   * `navigator.serviceWorker.ready` resolves once a registration has an ACTIVE worker for
   * this page's scope, whether or not that worker controls this page — which is precisely the
   * state to test. If we are already controlled there is nothing to do, and this costs one
   * property read.
   *
   * ⚠️ IT CANNOT CAUSE A RELOAD, checked in the built bundle rather than assumed: the plugin
   * reloads from `controlling` only when `event.isUpdate` is true, and `isUpdate` is
   * `Boolean(navigator.serviceWorker.controller)` sampled at register time — false in exactly
   * this state. Claiming here fires `controllerchange` and nothing else.
   */
  void navigator.serviceWorker.ready
    .then((registration) => {
      if (navigator.serviceWorker.controller !== null) return;
      registration.active?.postMessage({ type: 'CLAIM' });
    })
    .catch((error: unknown) => {
      // Best-effort, and said rather than swallowed: failing to ask costs the offline tier
      // for this session, which is worth a ledger line even though nothing can be retried.
      ledger.record(error, 'sw-claim', { notified: false });
    });
}
